// src/app-scenarios.ts — the /scenarios frontend. Vanilla, no framework, mirrors
// src/app.ts: boots from /api/snapshot, re-solves with the shared pure solveEcon,
// shows provenance on every number, carries state in the URL, and bridges from a
// capex scenario when arrived from the capex page. Chart.js is global (CDN).

import {
  baseEconParams,
  baselineValues,
  bridge,
  BRIDGE_DEFAULTS,
  BRIDGE_PARAMS,
  coldStartBaseline,
  econScenarioInputs,
  solveEcon,
  type BridgeParams,
  type EconBaseline,
  type EconBaselineValues,
  type EconInputs,
  type EconParams,
  type Flag,
} from "./econ/index.ts";
import {
  baseDrivers,
  solve,
  type Drivers,
  type World,
} from "./model/index.ts";
import { coldStartSnapshot, type SnapshotPayload } from "./snapshot.ts";
import { capexFromParams, econFromParams, econToParams, capexToParams } from "./urlstate.ts";
import ref from "../data/econ/reference_scenarios.json";
import aei from "../data/econ/aei_exposure.json";

/* eslint-disable @typescript-eslint/no-explicit-any */
const Chart: any = (window as any).Chart;

// ---- controls ----
interface Ctrl { k: keyof EconInputs; lab: string; min: number; max: number; step: number; scale: number; unit: string; hint: string; }
const CTRLS: Ctrl[] = [
  { k: "capabilities", lab: "Capabilities", min: 0, max: 100, step: 1, scale: 100, unit: "%", hint: "Share of knowledge-work tasks AI can do. 100% = AI can do all of them." },
  { k: "adoption", lab: "Adoption", min: 0, max: 100, step: 1, scale: 100, unit: "%", hint: "Of the tasks AI can do, the share where it is actually used in production." },
  { k: "autonomy", lab: "Autonomy", min: 0, max: 100, step: 1, scale: 100, unit: "%", hint: "Of AI-used tasks, the share done without a human in the loop (automation vs augmentation)." },
  { k: "productivity", lab: "Productivity", min: 1, max: 10, step: 0.5, scale: 1, unit: "×", hint: "Output multiplier on AI-affected tasks. 1× = no gain." },
  { k: "adjustment_months", lab: "Adjustment speed", min: 1, max: 36, step: 1, scale: 1, unit: " mo", hint: "Months for a displaced worker to find new work. Longer = more friction, higher unemployment." },
];

// ---- state ----
let snap: SnapshotPayload;
let world: World;
let baseline: EconBaseline;
let baseVals: EconBaselineValues;
let inputs: EconInputs;
let params: EconParams;
let bridgeParams: BridgeParams;
let capexDrivers: Drivers;
let fromCapex = false;
let breakLink = false;
let active = "default";
const userOverrides = new Set<string>();

let shareChart: any = null, unempChart: any = null, wageChart: any = null, tasksChart: any = null;

// ---- formatting ----
const pct = (v: number, d = 1): string => (v >= 0 ? "+" : "") + (v * 100).toFixed(d) + "%";
const pctAbs = (v: number, d = 1): string => (v * 100).toFixed(d) + "%";
const cls = (v: number): string => (v > 0.0005 ? "pos" : v < -0.0005 ? "neg" : "neu");
function provBadge(flag: Flag): string {
  const label = flag === "ESTIMATE" ? "EST" : flag;
  return '<span class="prov prov-' + flag + '">' + label + "</span>";
}

/** The un-bridged default scenario (used for reset + base of the bridge). */
function defaultInputs(): EconInputs {
  return { ...BRIDGE_DEFAULTS, adjustment_months: 12, new_task_share: 0.05, horizon_year: 2030 };
}

function capexRatio(): number {
  return solve(capexDrivers, world).cx;
}

/** The driver-default provenance: USER if overridden, ESTIMATE otherwise. */
function driverFlag(k: keyof EconInputs): Flag {
  return userOverrides.has("in:" + k) ? "USER" : "ESTIMATE";
}

// ---- controls ----
function buildControls(): void {
  const el = document.getElementById("controls")!;
  el.innerHTML = '<div class="ghead">The five drivers</div>';
  CTRLS.forEach((c) => {
    const val = (inputs[c.k] as number) * c.scale;
    const disp = c.scale === 1 ? val.toFixed(c.step < 1 ? 1 : 0) : Math.round(val);
    const d = document.createElement("div");
    d.className = "ctl";
    d.innerHTML =
      '<label><span>' + c.lab + " " + provBadge(driverFlag(c.k)) + '</span><b id="v_' + c.k + '">' + disp + c.unit + "</b></label>" +
      '<input type="range" id="r_' + c.k + '" min="' + c.min + '" max="' + c.max + '" step="' + c.step + '" value="' + val + '">' +
      '<div class="hint">' + c.hint + "</div>";
    el.appendChild(d);
    d.querySelector("input")!.addEventListener("input", (e) => {
      const v = parseFloat((e.target as HTMLInputElement).value) / c.scale;
      (inputs[c.k] as number) = v;
      userOverrides.add("in:" + c.k);
      active = "custom";
      document.getElementById("v_" + c.k)!.textContent =
        (c.scale === 1 ? v.toFixed(c.step < 1 ? 1 : 0) : Math.round(v * c.scale)) + c.unit;
      buildControls();
      renderChips();
      render();
      syncUrl();
    });
  });
}

function renderChips(): void {
  const el = document.getElementById("chips")!;
  el.innerHTML = "";
  const presets: Array<[string, string]> = [["modest", "Modest"], ["substantial", "Substantial"], ["extreme", "Extreme"]];
  for (const [id, label] of presets) {
    const b = document.createElement("button");
    b.className = "chip" + (active === id ? " on" : "");
    b.textContent = label;
    b.onclick = () => {
      inputs = econScenarioInputs(id as any);
      active = id;
      for (const x of [...userOverrides]) if (x.startsWith("in:")) userOverrides.delete(x);
      buildControls(); renderChips(); render(); syncUrl();
    };
    el.appendChild(b);
  }
  // From my capex model — enabled only when arrived from capex and link unbroken.
  const cb = document.createElement("button");
  cb.className = "chip" + (active === "capex" ? " on" : "");
  cb.textContent = "From my capex model";
  cb.disabled = !fromCapex || breakLink;
  cb.title = cb.disabled ? "Arrive from the capex model to enable this" : "Set defaults from your capex scenario via the bridge";
  cb.onclick = () => {
    applyCapexBridge();
    active = "capex";
    for (const x of [...userOverrides]) if (x.startsWith("in:")) userOverrides.delete(x);
    buildControls(); renderChips(); render(); syncUrl();
  };
  el.appendChild(cb);
  if (active === "custom") {
    const c = document.createElement("button");
    c.className = "chip on"; c.textContent = "Custom"; el.appendChild(c);
  }
}

function applyCapexBridge(): void {
  const b = bridge(capexRatio(), bridgeParams);
  inputs = {
    ...defaultInputs(),
    capabilities: b.capabilities_default,
    adoption: b.adoption_default,
    autonomy: b.autonomy_default,
    productivity: b.productivity_default,
  };
}

// ---- KPIs ----
function renderKPIs(): void {
  const o = solveEcon(inputs, baseVals, params);
  const set = (id: string, text: string, c?: string) => {
    const el = document.getElementById(id)!;
    el.textContent = text;
    if (c) el.className = "v " + c;
  };
  set("k_gdp", pct(o.gdp_uplift_pct), cls(o.gdp_uplift_pct));
  set("k_ls", pctAbs(o.labor_share));
  (document.getElementById("k_ls")!.nextElementSibling as HTMLElement).textContent = "capital share " + pctAbs(o.capital_share);
  set("k_u", pctAbs(o.unemployment.total));
  set("k_wk", pct(o.wage_change_pct.knowledge), cls(o.wage_change_pct.knowledge));
  set("k_wo", pct(o.wage_change_pct.other), cls(o.wage_change_pct.other));
}

// ---- charts ----
function renderShare(): void {
  const o = solveEcon(inputs, baseVals, params);
  document.getElementById("shareNote")!.innerHTML =
    "Labor share <b>" + pctAbs(o.labor_share) + "</b> · capital share <b>" + pctAbs(o.capital_share) +
    "</b>. Baseline labor share " + pctAbs(baseVals.labor_share) + " " + provBadge(baseline.labor_share.flag) + ".";
  if (!Chart) return;
  const ctx = document.getElementById("shareChart");
  if (shareChart) shareChart.destroy();
  shareChart = new Chart(ctx, {
    type: "doughnut",
    data: { labels: ["Labor", "Capital"], datasets: [{ data: [o.labor_share, o.capital_share], backgroundColor: ["#2a78d6", "#eda100"], borderWidth: 0 }] },
    options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "bottom", labels: { font: { size: 12 } } }, tooltip: { callbacks: { label: (c: any) => c.label + ": " + (c.parsed * 100).toFixed(1) + "%" } } } },
  });
}

function renderFlows(): void {
  const f = solveEcon(inputs, baseVals, params).worker_flows;
  const rows: Array<[string, number, string]> = [
    ["Stayed in role", f.stayed, "#1baf7a"],
    ["Crossed over", f.crossed_over, "#2a78d6"],
    ["Still to move", f.still_to_move, "#eda100"],
    ["Displaced", f.displaced, "#d03b3b"],
  ];
  const mx = Math.max(...rows.map((r) => r[1])) || 1;
  document.getElementById("flows")!.innerHTML = rows.map(([lab, v, col]) =>
    '<div class="flowbar"><span>' + lab + '</span><span><span class="fb" style="width:' + ((v / mx) * 100).toFixed(1) + "%;background:" + col + '"></span></span><span style="text-align:right">' + (v * 100).toFixed(1) + "%</span></div>",
  ).join("");
}

function bar(id: string, prev: any, labels: string[], data: number[], colors: string[], fmt: (v: number) => string): any {
  if (!Chart) return null;
  const ctx = document.getElementById(id);
  if (prev) prev.destroy();
  return new Chart(ctx, {
    type: "bar",
    data: { labels, datasets: [{ data, backgroundColor: colors, borderRadius: 3, maxBarThickness: 48 }] },
    options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c: any) => fmt(c.parsed.y) } } },
      scales: { x: { grid: { display: false }, border: { display: false }, ticks: { font: { size: 11.5 } } }, y: { grid: { color: "rgba(128,128,128,.16)" }, border: { display: false }, ticks: { font: { size: 11 }, callback: fmt } } } },
  });
}

function renderUnemp(): void {
  const u = solveEcon(inputs, baseVals, params).unemployment;
  unempChart = bar("unempChart", unempChart, ["Knowledge", "Other", "Total"], [u.knowledge, u.other, u.total], ["#6250d6", "#2a78d6", "#14140f"], (v) => (v * 100).toFixed(1) + "%");
}
function renderWage(): void {
  const w = solveEcon(inputs, baseVals, params).wage_change_pct;
  wageChart = bar("wageChart", wageChart, ["Knowledge", "Other", "Average"], [w.knowledge, w.other, w.average], [w.knowledge >= 0 ? "#1baf7a" : "#d03b3b", w.other >= 0 ? "#1baf7a" : "#d03b3b", w.average >= 0 ? "#1baf7a" : "#d03b3b"], (v) => (v >= 0 ? "+" : "") + (v * 100).toFixed(1) + "%");
}
function renderTasks(): void {
  const t = solveEcon(inputs, baseVals, params).tasks;
  tasksChart = bar("tasksChart", tasksChart, ["Unchanged", "Augmented", "Automated", "New"], [t.unchanged, t.augmented, t.automated, t.new], ["#8b8a82", "#2a78d6", "#d03b3b", "#1baf7a"], (v) => (v * 100).toFixed(0) + "%");
}

// ---- reference points ----
function renderRefpoints(): void {
  const scen = ref.scenarios as Record<string, any>;
  let h = "<thead><tr><th>Scenario</th><th>GDP uplift</th><th>Labor share</th><th>Capital share</th><th>Knowledge wages</th></tr></thead><tbody>";
  for (const id of ["modest", "substantial", "extreme"]) {
    const s = scen[id];
    const wk = s.targets.wage_knowledge_dir;
    const wkT = wk === "down_gt10" ? "fall >10%" : wk === "flat" ? "flat" : "flat / up";
    h += "<tr><td>" + s.label + "</td><td>" + pct(s.targets.gdp_uplift_pct) + "</td><td>" + pctAbs(s.targets.labor_share) + "</td><td>" + pctAbs(s.targets.capital_share) + "</td><td>" + wkT + "</td></tr>";
  }
  document.getElementById("refpoints")!.innerHTML = h + "</tbody>";
}

// ---- baseline ----
function renderBaseline(): void {
  const b = baseline;
  const rows: Array<[string, string, typeof b.gdp]> = [
    ["Real GDP", "$" + (b.gdp.value / 1000).toFixed(1) + "T", b.gdp],
    ["Unemployment", pctAbs(b.unemployment.value), b.unemployment],
    ["Labor share", pctAbs(b.labor_share.value), b.labor_share],
    ["Employment", (b.employment.value / 1000).toFixed(1) + "M", b.employment],
    ["Knowledge-worker share", pctAbs(b.knowledge_worker_share.value), b.knowledge_worker_share],
    ["Job openings", (b.openings.value / 1000).toFixed(2) + "M", b.openings],
    ["Hires", (b.hires.value / 1000).toFixed(2) + "M", b.hires],
    ["Separations", (b.separations.value / 1000).toFixed(2) + "M", b.separations],
  ];
  let h = "<thead><tr><th>Measure</th><th>Value</th><th>Source</th><th>As of</th></tr></thead><tbody>";
  for (const [lab, val, f] of rows) {
    h += "<tr><td>" + lab + "</td><td>" + val + " " + provBadge(f.flag) + "</td><td class='neu'>" + f.source + "</td><td class='neu'>" + f.as_of + "</td></tr>";
  }
  document.getElementById("baseline")!.innerHTML = h + "</tbody>";
}

// ---- freshness strip ----
function renderFreshstrip(): void {
  const b = baseline;
  const fams: Array<[string, Array<typeof b.gdp>]> = [
    ["FRED macro", [b.gdp, b.unemployment, b.labor_share, b.employment]],
    ["JOLTS", [b.openings, b.hires, b.separations]],
    ["OEWS", [b.knowledge_worker_share]],
  ];
  let h = "";
  for (const [name, fields] of fams) {
    const stale = fields.some((f) => f.flag === "STALE");
    const live = fields.some((f) => f.flag === "LIVE");
    const asof = fields.map((f) => f.as_of).sort().reverse()[0];
    h += '<span class="pill ' + (stale ? "stale" : live ? "live" : "") + '">' + name + " · " + asof + "</span>";
  }
  h += '<span class="pill">Econ Index · ' + aei.as_of + "</span>";
  h += '<span class="pill">O*NET · sample</span>';
  document.getElementById("freshstrip")!.innerHTML = h;
}

// ---- assumptions (editable) ----
function renderAssumptions(): void {
  let c = "";
  (Object.keys(params) as Array<keyof EconParams>).forEach((k) => {
    const co = params[k];
    const flag: Flag = userOverrides.has("coef:" + k) ? "USER" : co.flag;
    c += '<div class="srow"><span style="flex:1"><b style="font-weight:500">' + co.lab + " " + provBadge(flag) + '</b><div style="font-size:11.5px;color:var(--ink3);line-height:1.45">' + co.hint + "</div></span>" +
      '<input type="number" step="0.01" value="' + co.v + '" data-k="' + k + '" class="acoef" style="margin-left:12px"></div>';
  });
  document.getElementById("coefs")!.innerHTML = c;

  let g = '<div class="note" style="margin:0 0 8px">' + bridge(capexRatio(), bridgeParams).rationale + "</div>";
  (Object.keys(bridgeParams) as Array<keyof BridgeParams>).forEach((k) => {
    g += '<div class="srow"><span style="flex:1"><b style="font-weight:500">' + k + " " + provBadge(userOverrides.has("br:" + k) ? "USER" : "ESTIMATE") + "</b></span>" +
      '<input type="number" step="0.05" value="' + bridgeParams[k] + '" data-k="' + k + '" class="abr" style="margin-left:12px"></div>';
  });
  document.getElementById("bridgeCoefs")!.innerHTML = g;

  document.getElementById("sources")!.innerHTML =
    "<b>What-if, not a forecast.</b> This is an independent, open implementation inspired by and calibrated against Anthropic's published reference scenarios (Korinek et al., 2026); it is not endorsed by Anthropic.<br><br>" +
    "<b>Live vs estimated.</b> GDP, unemployment, labor share, employment and job flows refresh from FRED on the same path as the capex model; each carries a provenance badge and an <i>as of</i> date. The knowledge-worker share is derived from OEWS via a documented, editable mapping (ESTIMATE). Adoption defaults are informed by the Anthropic Economic Index. All model coefficients are calibrated to three published points, not estimated from micro-data — override them freely (that flips the row to USER).<br><br>" +
    "<b>What this model does not do</b>: policy responses, business cycles, aggregate demand, the data-centre build-out's own demand, robotics, individual-worker trajectories, catastrophic risks, and any feedback from the labor side back into the capex model. See the <a href='/scenarios/methodology'>methodology</a>.";

  document.querySelectorAll<HTMLInputElement>(".acoef").forEach((el) => {
    el.onchange = () => {
      const k = el.dataset.k! as keyof EconParams;
      params[k].v = parseFloat(el.value) || 0;
      userOverrides.add("coef:" + k);
      renderAssumptions(); render();
    };
  });
  document.querySelectorAll<HTMLInputElement>(".abr").forEach((el) => {
    el.onchange = () => {
      const k = el.dataset.k! as keyof BridgeParams;
      bridgeParams[k] = parseFloat(el.value) || 0;
      userOverrides.add("br:" + k);
      renderAssumptions(); render();
    };
  });
}

// ---- cross-module chip + return banner ----
function renderXchip(): void {
  const r = solve(capexDrivers, world);
  const el = document.getElementById("xchip")!;
  el.textContent = "System capex: $" + r.sysCapex.toFixed(0) + "B (" + (r.cx * 100).toFixed(0) + "% of base)";
}

function renderReturnBanner(): void {
  const el = document.getElementById("returnbanner")!;
  if (fromCapex && !breakLink) {
    const r = solve(capexDrivers, world);
    const back = "/?" + capexToParams(capexDrivers).map(([k, v]) => k + "=" + v).join("&");
    el.className = "ret on";
    el.innerHTML =
      "Starting from your capex scenario: capex at <b>" + (capexDrivers.capex * 100).toFixed(0) + "% of base</b> · system capex $" + r.sysCapex.toFixed(0) + "B. " +
      '<a href="#" id="breaklink2">Break link</a> · <a href="' + back + '">Back to capex model →</a>';
    document.getElementById("breaklink2")!.onclick = (e) => { e.preventDefault(); (document.getElementById("breaklink") as HTMLInputElement).checked = true; breakLink = true; onBreakLink(); };
  } else {
    el.className = "ret";
  }
}

function onBreakLink(): void {
  renderReturnBanner(); renderChips(); syncUrl();
}

// ---- header / banner ----
function renderHeader(): void {
  const asof = document.getElementById("asof")!;
  asof.textContent = "Baseline as of " + (snap.econ?.unemployment.as_of ?? snap.asOf.slice(0, 10)) + " · generated " + snap.generatedAt.slice(0, 19).replace("T", " ") + "Z";
  const banner = document.getElementById("banner")!;
  if (snap.stale) {
    banner.className = "banner on";
    banner.textContent = "⚠ Some live fields are STALE — a data refresh failed; showing last-known values. Check the provenance badges.";
  } else banner.className = "banner";
}

// ---- URL ----
function syncUrl(): void {
  const parts = econToParams(inputs).map(([k, v]) => k + "=" + v);
  if (fromCapex) {
    parts.push("from=capex");
    parts.push(...capexToParams(capexDrivers).map(([k, v]) => k + "=" + v));
  }
  const url = location.pathname + "?" + parts.join("&");
  history.replaceState(null, "", url);
}

// ---- top-level render ----
function render(): void {
  renderKPIs();
  renderShare();
  renderFlows();
  renderUnemp();
  renderWage();
  renderTasks();
}

async function boot(): Promise<void> {
  try {
    const res = await fetch("/api/snapshot");
    if (!res.ok) throw new Error("http " + res.status);
    snap = (await res.json()) as SnapshotPayload;
  } catch {
    snap = coldStartSnapshot(new Date().toISOString());
    snap.stale = true;
  }
  world = snap.world;
  baseline = snap.econ ?? coldStartBaseline();
  baseVals = baselineValues(baseline);
  params = baseEconParams();
  bridgeParams = { ...BRIDGE_PARAMS };

  const q = new URLSearchParams(location.search);
  fromCapex = q.get("from") === "capex";
  capexDrivers = fromCapex ? capexFromParams(q, baseDrivers()) : { ...snap.drivers };

  // Decode inputs: bridged defaults when arriving from capex, else page defaults,
  // then apply any explicit econ params from the URL on top.
  let base: EconInputs;
  if (fromCapex) { applyCapexBridge(); base = inputs; } else { base = defaultInputs(); }
  inputs = econFromParams(q, base);
  active = fromCapex ? "capex" : "default";
  if (hasExplicitEcon(q)) active = "custom";

  document.getElementById("reset")!.onclick = () => {
    inputs = defaultInputs(); active = "default";
    for (const x of [...userOverrides]) if (x.startsWith("in:")) userOverrides.delete(x);
    buildControls(); renderChips(); render(); syncUrl();
  };
  const bl = document.getElementById("breaklink") as HTMLInputElement;
  bl.onchange = () => { breakLink = bl.checked; onBreakLink(); };
  document.getElementById("copylink")!.onclick = () => {
    navigator.clipboard?.writeText(location.href);
    const btn = document.getElementById("copylink")!;
    const t = btn.textContent; btn.textContent = "Copied ✓";
    setTimeout(() => (btn.textContent = t), 1400);
  };

  renderHeader();
  renderReturnBanner();
  renderFreshstrip();
  renderBaseline();
  renderRefpoints();
  buildControls();
  renderChips();
  renderAssumptions();
  renderXchip();
  render();
  syncUrl();
}

function hasExplicitEcon(q: URLSearchParams): boolean {
  return ["ca", "ad", "au", "pr", "aj", "nt", "hz"].some((k) => q.has(k));
}

boot();
